import React, { useState } from "react";
import useApi from "@/hooks/useApi";
import useSharedSchedules from "@/hooks/useSharedSchedules";
import useToastAlert from "@/hooks/useToastAlert";
import { PrimaryBlueButton, Severity } from "@velaro/velaro-component-lib";
import { SharedSchedule, ScheduleWindow } from "@/context/SharedSchedulesContext";
import ScheduleEditor from "./ScheduleEditor";

type Mode = "list" | "create" | "edit";

export default function Schedules() {
  const api = useApi();
  const { schedules, loading, reload } = useSharedSchedules();
  const { displayToast } = useToastAlert();

  const [mode, setMode] = useState<Mode>("list");
  const [editTarget, setEditTarget] = useState<SharedSchedule | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [windows, setWindows] = useState<ScheduleWindow[]>([]);

  function startCreate() {
    setName("");
    setTimezone("");
    setWindows([
      { daysOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], fromTime: "09:00", toTime: "17:00" }
    ]);
    setEditTarget(null);
    setMode("create");
  }

  function startEdit(schedule: SharedSchedule) {
    setName(schedule.name);
    setTimezone(schedule.timezone ?? "");
    setWindows(schedule.windows ?? []);
    setEditTarget(schedule);
    setMode("edit");
  }

  async function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body = { name: name.trim(), timezone: timezone || null, windows };
      if (mode === "create") {
        await api.messaging.post("SharedSchedule", body);
        displayToast(Severity.Success, "Schedule created.");
      } else if (mode === "edit" && editTarget) {
        await api.messaging.put(`SharedSchedule/${editTarget.id}`, body);
        displayToast(Severity.Success, "Schedule updated.");
      }
      await reload();
      setMode("list");
    } catch {
      displayToast(Severity.Error, "Failed to save schedule.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(schedule: SharedSchedule) {
    if (!confirm(`Delete "${schedule.name}"? Any teams or rules using this schedule will lose the link.`)) return;
    try {
      await api.messaging.delete(`SharedSchedule/${schedule.id}`);
      displayToast(Severity.Success, "Schedule deleted.");
      await reload();
    } catch {
      displayToast(Severity.Error, "Failed to delete schedule.");
    }
  }

  if (mode === "create" || mode === "edit") {
    return (
      <div className="p-6 max-w-2xl">
        <h2 className="text-lg font-semibold mb-4">
          {mode === "create" ? "New Schedule" : `Edit: ${editTarget?.name}`}
        </h2>
        <ScheduleEditor
          name={name}
          timezone={timezone}
          windows={windows}
          onNameChange={setName}
          onTimezoneChange={setTimezone}
          onWindowsChange={setWindows}
          onSave={handleSave}
          onCancel={() => setMode("list")}
          saving={saving}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold">Schedule Library</h1>
          <p className="text-sm text-gray-500 mt-1">
            Named schedules that can be linked to teams and referenced in routing rules.
            Change a schedule once and it updates everywhere it's used.
          </p>
        </div>
        <PrimaryBlueButton label="+ New Schedule" onClick={startCreate} />
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : schedules.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-400">
          <p className="text-sm">No schedules yet.</p>
          <p className="text-xs mt-1">
            Create a named schedule like "Business Hours" and link it to teams and routing rules.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 max-h-[600px] overflow-y-auto">
          {schedules.map((s) => (
            <div
              key={s.id}
              className="border border-gray-200 rounded-lg p-4 flex items-start justify-between"
            >
              <div>
                <div className="font-medium">{s.name}</div>
                {s.timezone && (
                  <div className="text-xs text-gray-400 mt-0.5">{s.timezone}</div>
                )}
                <div className="text-sm text-gray-500 mt-1">
                  {(s.windows ?? []).length === 0
                    ? "No time windows defined"
                    : (s.windows ?? []).map((w, i) => (
                        <span key={i} className="mr-2">
                          {w.daysOfWeek.map((d) => d.slice(0, 3)).join(", ")}{" "}
                          {w.fromTime}–{w.toTime}
                        </span>
                      ))}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  className="text-blue-500 hover:text-blue-700 text-sm"
                  onClick={() => startEdit(s)}
                >
                  Edit
                </button>
                <button
                  className="text-red-400 hover:text-red-600 text-sm"
                  onClick={() => handleDelete(s)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
